import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import {
  BODY,
  BODY_SMALL,
  HEADING_2,
} from "@/components/marketing/marketingType";
import { NOT_WIDE, WIDE } from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import { LinkedText } from "../LandingPrimitives";
import { Lines } from "@/components/marketing/Lines";

export type Cards = Laid<"cards">;

/**
 * `.benefit_title_wrap.is_use-case`: a title published as three phrases in a
 * row, 6.4 apart, each wrapping as a unit once the row wraps below 992. One
 * heading element carries them, so the outline reads the sentence whole.
 */
const PHRASE_ROW = {
  display: "flex",
  columnGap: "6.4px",
  rowGap: "6.4px",
  [NOT_WIDE]: { flexWrap: "wrap" },
} satisfies SxProps<Theme>;

/**
 * The heading pair a card band opens on, and not `SectionHeading`: every
 * published card band sets its own step under the title (8 on the benefits,
 * product and picture bands, 12.8 on the key-features panel) and its own
 * measure, where the shared heading has one of each.
 */
/**
 * The title, as one heading: the published three phrases in a row where the
 * block carries them, its text otherwise.
 */
function BandTitle({
  block,
  align,
}: {
  block: Cards;
  align: "center" | "left";
}) {
  const level = block.level === 3 ? "h3" : "h2";
  const size = block.level === 3 ? { fontSize: 28 } : {};
  if (block.titleParts) {
    return (
      <Typography
        component={level}
        sx={[
          HEADING_2,
          size,
          PHRASE_ROW,
          { justifyContent: align === "center" ? "center" : "flex-start" },
        ]}
      >
        {block.titleParts.map((part, index) => (
          <Box component="span" key={index}>
            {part}
          </Box>
        ))}
      </Typography>
    );
  }
  return block.title ? (
    <Typography component={level} sx={[HEADING_2, size]}>
      <Lines text={block.title} />
    </Typography>
  ) : null;
}

export function BandHeading({
  block,
  gap,
  align = "center",
  lines,
  rail,
  wideRail,
}: {
  block: Cards;
  /** The published step between the title and the line under it. */
  gap: string;
  align?: "center" | "left";
  /** Where a centred pair keeps its wrapped lines left, as a wrap that sets no alignment does. */
  lines?: "left";
  /** The measure the published wrap caps its copy at, at every width. */
  rail?: string;
  /** The same, where the published cap is a wide-step rule only. */
  wideRail?: string;
}) {
  const title = block.titleParts ? block.titleParts.join(" ") : block.title;
  if (!title && !block.body) {
    return null;
  }
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap,
        alignItems: align === "center" ? "center" : "flex-start",
        textAlign: lines ?? align,
        width: 1,
        mx: "auto",
        maxWidth: rail,
        ...(wideRail === undefined ? {} : { [WIDE]: { maxWidth: wideRail } }),
      }}
    >
      <BandTitle block={block} align={align} />
      {block.body ? (
        <Typography sx={block.bodyTone === "small" ? BODY_SMALL : BODY}>
          <LinkedText text={block.body} links={block.links} />
        </Typography>
      ) : null}
    </Box>
  );
}
